import { z } from 'zod';

/** Reusable refinement: reject strings containing HTML angle brackets (safety net for paths bypassing sanitize middleware) */
const noHtml = (field: string) =>
  z.string().refine((val) => !/[<>]/.test(val), {
    message: `${field} must not contain HTML characters`,
  });

const freeEmailDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'aol.com',
  'mail.com',
  'protonmail.com',
  'zoho.com',
];

export const businessSchema = z.object({
  name: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(200, 'Company name must be at most 200 characters')
    .pipe(noHtml('Company name')),
  legalEntity: z.string().min(2, 'Legal entity is required').max(500, 'Legal entity must be at most 500 characters')
    .pipe(noHtml('Legal entity')),
  registrationNumber: z
    .string()
    .min(1, 'Registration number is required')
    .max(500, 'Registration number must be at most 500 characters')
    .regex(
      /^[A-Za-z0-9\-./]+$/,
      'Registration number must be alphanumeric (dots, hyphens, slashes allowed)',
    ),
  vatNumber: z.string().max(500, 'VAT number must be at most 500 characters').optional(),
  businessType: z.string().min(2, 'Business type is required').max(500, 'Business type must be at most 500 characters')
    .pipe(noHtml('Business type')),
  industry: z.string().min(2, 'Industry is required').max(500, 'Industry must be at most 500 characters')
    .pipe(noHtml('Industry')),
});

export const locationSchema = z.object({
  country: z.string().min(2, 'Country is required').max(500, 'Country must be at most 500 characters')
    .pipe(noHtml('Country')),
  city: z.string().min(2, 'City is required').max(500, 'City must be at most 500 characters')
    .pipe(noHtml('City')),
  street: z.string().min(5, 'Street address must be at least 5 characters').max(500, 'Street address must be at most 500 characters')
    .pipe(noHtml('Street')),
  postalCode: z.string().min(2, 'Postal code is required').max(500, 'Postal code must be at most 500 characters'),
  region: z.string().max(500, 'Region must be at most 500 characters').optional(),
});

export const contactSchema = z.object({
  primaryEmail: z
    .string()
    .max(500, 'Email must be at most 500 characters')
    .email('Invalid email address')
    .refine(
      (email) => !freeEmailDomains.some((d) => email.toLowerCase().endsWith(`@${d}`)),
      'Business email required (not Gmail, Yahoo, Hotmail, etc.)'
    ),
  supportEmail: z.string().max(500, 'Email must be at most 500 characters').email('Invalid support email address'),
  legalEmail: z.string().max(500, 'Email must be at most 500 characters').email('Invalid legal email address'),
  privacyEmail: z.string().max(500, 'Email must be at most 500 characters').email('Invalid privacy email address'),
  phone: z
    .string()
    .max(500, 'Phone number must be at most 500 characters')
    .regex(/^\+\d{1,3}\s?\d{6,14}$/, 'Phone must include country code (e.g., +1 1234567890)'),
  businessHours: z.string().min(5, 'Business hours are required').max(500, 'Business hours must be at most 500 characters'),
});

export const websiteSchema = z.object({
  domain: z
    .string()
    .max(2048, 'Domain must be at most 2048 characters')
    .regex(
      /^[a-z0-9]+([-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i,
      'Invalid domain format (e.g., example.com)'
    ),
  language: z.string().min(2, 'Language is required').max(500, 'Language must be at most 500 characters'),
  languageCode: z
    .string()
    .max(500, 'Language code must be at most 500 characters')
    .regex(/^[a-z]{2}-[A-Z]{2}$/, 'Invalid language code format (e.g., en-US)'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AED', 'AUD', 'NZD', 'SGD', 'HKD', 'TWD', 'JPY', 'RUB', 'KZT', 'BYN', 'NOK', 'DKK', 'SEK', 'TRY', 'INR', 'ZAR', 'PLN', 'CZK', 'CAD', 'BRL', 'MXN', 'ARS', 'CLP', 'COP']),
  targetRegion: z.enum(['EU', 'US', 'UK', 'UAE', 'APAC', 'CIS', 'LATAM', 'Nordic', 'Global']),
});

export const designSchema = z.object({
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color format'),
  fontFamily: z.enum([
    // Classic sans options (backward compat)
    'Inter',
    'Roboto',
    'Open Sans',
    'Poppins',
    // Premium pairings — serif display + sans body, or mixed
    'Inter + Fraunces',
    'Geist + Instrument Serif',
    'Manrope + Playfair Display',
    'Space Grotesk + Lora',
    'Plus Jakarta Sans + Fraunces',
    'DM Sans + DM Serif Display',
  ]),
  borderRadius: z.enum(['none', 'sm', 'md', 'lg']),
  stylePreset: z.enum(['corporate', 'minimal-light', 'gradient', 'nature', 'luxury', 'modern']),
});

export const generateRequestSchema = z.object({
  business: businessSchema,
  location: locationSchema,
  contact: contactSchema,
  website: websiteSchema,
  design: designSchema,
});

export const autoGenerateRequestSchema = z.object({
  country: z.string().min(2, 'Country is required').max(500, 'Country must be at most 500 characters')
    .pipe(noHtml('Country')),
  city: z.string().min(2, 'City is required').max(500, 'City must be at most 500 characters')
    .pipe(noHtml('City')),
});

export type BusinessSchema = z.infer<typeof businessSchema>;
export type LocationSchema = z.infer<typeof locationSchema>;
export type ContactSchema = z.infer<typeof contactSchema>;
export type WebsiteSchema = z.infer<typeof websiteSchema>;
export type DesignSchema = z.infer<typeof designSchema>;
export type GenerateRequestSchema = z.infer<typeof generateRequestSchema>;
export type AutoGenerateRequestSchema = z.infer<typeof autoGenerateRequestSchema>;
